// src/belediye/components/notifications/notificationSound.js
//
// FAZ-33 — Bildirim sesi. Harici asset / CDN YOK: kısa "çın" WebAudio ile
// sentezlenir (tam self-contained, CSP-güvenli). Autoplay politikası için ilk
// kullanıcı etkileşimine kadar "kilitli"; ilk pointerdown/keydown'da açılır
// (login zaten bir etkileşimdir). Çoklu bildirimde tek ses için debounce.

let _ctx = null;
let _unlocked = false;
let _lastPlayed = 0;
const DEBOUNCE_MS = 2000;

function _getCtx() {
  if (_ctx) return _ctx;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    _ctx = new Ctx();
    return _ctx;
  } catch {
    return null;
  }
}

function _unlock() {
  const ctx = _getCtx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume();
    _unlocked = true;
  } catch {
    /* yut */
  }
}

// İlk etkileşimde autoplay kilidini aç (bir kez).
export function installSoundUnlock() {
  if (typeof window === "undefined") return;
  const handler = () => {
    _unlock();
    window.removeEventListener("pointerdown", handler);
    window.removeEventListener("keydown", handler);
  };
  window.addEventListener("pointerdown", handler, { once: false });
  window.addEventListener("keydown", handler, { once: false });
}

export function isSoundUnlocked() {
  return _unlocked;
}

// Kısa iki-tonlu "çın" (natural bir bildirim tınısı).
export function playNotifySound() {
  if (!_unlocked) return false;
  const now = Date.now();
  if (now - _lastPlayed < DEBOUNCE_MS) return false; // debounce
  const ctx = _getCtx();
  if (!ctx) return false;

  try {
    if (ctx.state === "suspended") ctx.resume();
    const t0 = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, t0);
    // Ses seviyesi bir tık yukarı (0.14 → 0.22) — daha belirgin "çın".
    master.gain.exponentialRampToValueAtTime(0.22, t0 + 0.02);
    master.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.6);
    master.connect(ctx.destination);

    // iki nota: C6 → E6 (kısa arpej)
    const notes = [
      { freq: 1046.5, start: 0.0, dur: 0.28 },
      { freq: 1318.5, start: 0.12, dur: 0.32 },
    ];
    for (const n of notes) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(n.freq, t0 + n.start);
      g.gain.setValueAtTime(0.0001, t0 + n.start);
      g.gain.exponentialRampToValueAtTime(1.0, t0 + n.start + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + n.start + n.dur);
      osc.connect(g);
      g.connect(master);
      osc.start(t0 + n.start);
      osc.stop(t0 + n.start + n.dur + 0.02);
    }

    _lastPlayed = now;
    return true;
  } catch {
    return false;
  }
}

// ── Mesajlaşma sesleri ────────────────────────────────────────────────
// Kurum içi mesajlaşma için gidiş/geliş sesleri. Bildirim "çın"ından AYRI
// tonlar (kullanıcı gönderdiğini/aldığını ayırt etsin) ve AYRI debounce
// (gönder→al kısa aralıkta ikisi de çalabilsin). Aynı AudioContext + autoplay
// kilidini (installSoundUnlock) paylaşır — ek asset/CDN yok.
let _lastSent = 0;
let _lastRecv = 0;

// Ortak ton çalıcı: her nota {freq, to?, start, dur}. to verilirse frekans
// kayar (glissando). master zarf tek "çın" gibi yumuşak iniş-çıkış yapar.
function _ring(notes, { peak = 0.2, tail = 0.5 } = {}) {
  const ctx = _getCtx();
  if (!ctx) return false;
  try {
    if (ctx.state === "suspended") ctx.resume();
    const t0 = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, t0);
    master.gain.exponentialRampToValueAtTime(peak, t0 + 0.02);
    master.gain.exponentialRampToValueAtTime(0.0001, t0 + tail);
    master.connect(ctx.destination);
    for (const n of notes) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(n.freq, t0 + n.start);
      if (n.to) osc.frequency.exponentialRampToValueAtTime(n.to, t0 + n.start + n.dur);
      g.gain.setValueAtTime(0.0001, t0 + n.start);
      g.gain.exponentialRampToValueAtTime(1.0, t0 + n.start + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + n.start + n.dur);
      osc.connect(g);
      g.connect(master);
      osc.start(t0 + n.start);
      osc.stop(t0 + n.start + n.dur + 0.02);
    }
    return true;
  } catch {
    return false;
  }
}

// Mesaj GİDİŞ — kısa, hafif, alçalan "poff" (bildirimden belirgin ayrı, sessiz).
export function playMessageSentSound() {
  if (!_unlocked) return false;
  const now = Date.now();
  if (now - _lastSent < 120) return false; // hızlı ardışık gönderimde tek ses
  const ok = _ring(
    [
      { freq: 660, to: 520, start: 0.0, dur: 0.09 },
      { freq: 520, start: 0.05, dur: 0.09 },
    ],
    { peak: 0.12, tail: 0.26 }
  );
  if (ok) _lastSent = now;
  return ok;
}

// Mesaj GELİŞ — parlak, yükselen kısa "din-dın" (gidişten ve bildirimden ayrı).
export function playMessageReceivedSound() {
  if (!_unlocked) return false;
  const now = Date.now();
  if (now - _lastRecv < 700) return false; // yakın-zamanlı çift yoklamayı birleştir
  const ok = _ring(
    [
      { freq: 740, start: 0.0, dur: 0.14 },
      { freq: 988, start: 0.085, dur: 0.2 },
    ],
    { peak: 0.2, tail: 0.5 }
  );
  if (ok) _lastRecv = now;
  return ok;
}
